import {
  FormattedShopifyProduct,
  FormattedShopifyCollection
} from '@portal/types/shopify/product.formatted.types';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { ALL_PRODUCTS_CATEGORY_TAB } from '@portal/shared/studio/ShopListNav/constants';
import { RefObject } from 'react';
import {
  scrollToSectionTop,
  setIsMembersOnlySectionLoading,
  setIsGenericSectionLoading,
  setGenericSectionRef,
  setMembersOnlySectionRef
} from './utils';
import {
  CategoryTab,
  MembersOnlyLoaderKey,
  MembersOnlyProductsSectionName,
  MembersOnlyRefName,
  ProductListStoreState
} from './types';

/**
 * zustand "devtools" middleware allows you to debug zustand states on local/stage
 * if you install "redux devtools" chrome extension on your browser
 *
 * according to zustand documentation:
 * https://zustand.docs.pmnd.rs/middlewares/devtools
 * the debugger defaults to "enabled" on development/stage env and "disabled"
 * on production env out of the box (no further env config needed)
 */

export const useProductListStore = create<ProductListStoreState>()(
  devtools(
    (set) => ({
      data: {
        currentActiveCategoryTab: {
          slug: ALL_PRODUCTS_CATEGORY_TAB,
          isDefaultProductSubCategory: false
        },
        loading: {
          isBannersSectionLoading: true,
          isBundlesSectionLoading: true,
          isProductsSectionLoading: true,
          membersOnly: {
            isAccessoriesSectionLoading: true
          },
          isLoadingCategoryPage: false,
          isPageLoaded: false
        },
        transition: {
          isTransitioning: false
        },
        pageRefs: {
          bannersSectionRef: undefined,
          bundlesSectionRef: undefined,
          productsSectionRef: undefined,
          hasMembersOnlySections: false,
          membersOnly: {
            accessoriesSectionRef: undefined
          }
        },
        shopify: {
          banners: [],
          bundles: [],
          products: [],
          membersOnly: {
            accessories: []
          }
        }
      },
      actions: {
        setCurrentActiveCategoryTab: (
          newTab: CategoryTab,
          options: {
            isInitialLoad: boolean;
            isPageReset: boolean;
          } = {
            isInitialLoad: false,
            isPageReset: false
          }
        ) =>
          set((state) => {
            const newState = { ...state };
            newState.data.currentActiveCategoryTab = newTab;
            if (options.isInitialLoad) {
              newState.data.loading.isLoadingCategoryPage = true;
            }
            if (!options.isInitialLoad && !options.isPageReset) {
              newState.data.transition.isTransitioning = true;
            }
            return newState;
          }),
        loading: {
          setIsBannersSectionLoading: (newValue: boolean, options: { isLoggedIn: boolean }) =>
            set((state) => {
              return setIsGenericSectionLoading(
                state,
                'isBannersSectionLoading',
                newValue,
                options
              );
            }),
          setIsBundlesSectionLoading: (newValue: boolean, options: { isLoggedIn: boolean }) =>
            set((state) => {
              return setIsGenericSectionLoading(
                state,
                'isBundlesSectionLoading',
                newValue,
                options
              );
            }),
          setIsProductsSectionLoading: (newValue: boolean, options: { isLoggedIn: boolean }) =>
            set((state) => {
              return setIsGenericSectionLoading(
                state,
                'isProductsSectionLoading',
                newValue,
                options
              );
            }),
          setIsMembersOnlyProductsSectionLoading: (
            loaderKey: MembersOnlyLoaderKey,
            newValue: boolean,
            options: { isLoggedIn: boolean }
          ) =>
            set((state) => {
              return setIsMembersOnlySectionLoading(state, loaderKey, newValue, options);
            })
        },
        transition: {
          setIsTransitioning: (
            newValue: boolean,
            options: {
              aboveSmall: boolean;
              isMembersOnlyProductSection: boolean;
              refName: MembersOnlyRefName;
            }
          ) =>
            set((state) => {
              const newState = { ...state };
              newState.data.transition.isTransitioning = newValue;
              if (!newValue) {
                scrollToSectionTop(newState, options);
              }
              return newState;
            })
        },
        pageRefs: {
          setBannersSectionRef: (ref: RefObject<HTMLDivElement | null>) =>
            set((state) => {
              return setGenericSectionRef(state, 'bannersSectionRef', ref);
            }),
          setBundlesSectionRef: (ref: RefObject<HTMLDivElement | null>) =>
            set((state) => {
              return setGenericSectionRef(state, 'bundlesSectionRef', ref);
            }),
          setProductsSectionRef: (ref: RefObject<HTMLDivElement | null>) =>
            set((state) => {
              return setGenericSectionRef(state, 'productsSectionRef', ref);
            }),
          setHasMembersOnlySections: (newValue: boolean) =>
            set((state) => {
              const newState = { ...state };
              newState.data.pageRefs.hasMembersOnlySections = newValue;
              return newState;
            }),
          setMembersOnlyProductsSectionRef: (
            refName: MembersOnlyRefName,
            ref: RefObject<HTMLDivElement | null>
          ) =>
            set((state) => {
              return setMembersOnlySectionRef(state, refName, ref);
            })
        },
        shopify: {
          setBanners: (newBanners: FormattedShopifyCollection[]) =>
            set((state) => {
              const newState = { ...state };
              newState.data.shopify.banners.push(newBanners);
              return newState;
            }),
          setBundles: (newBundles: FormattedShopifyCollection[]) =>
            set((state) => {
              const newState = { ...state };
              newState.data.shopify.bundles = newBundles;
              return newState;
            }),
          setProducts: (newProducts: FormattedShopifyProduct[]) =>
            set((state) => {
              const newState = { ...state };
              newState.data.shopify.products = newProducts;
              return newState;
            }),
          setMembersOnlyProducts: (
            sectionName: MembersOnlyProductsSectionName,
            newProducts: FormattedShopifyProduct[]
          ) =>
            set((state) => {
              const newState = { ...state };
              newState.data.shopify.membersOnly[sectionName] = newProducts;
              return newState;
            })
        }
      }
    }),
    {
      // remove actual ref objects from zustand state after they reach devtools
      // ref objects take up too much memory allocation for redux extensions to
      // handle, they actually crash the extension during page load
      stateSanitizer: (state: ProductListStoreState) => {
        const {
          bannersSectionRef,
          bundlesSectionRef,
          productsSectionRef,
          membersOnly: { accessoriesSectionRef }
        } = state.data.pageRefs;
        return {
          ...state,
          data: {
            ...state.data,
            pageRefs: {
              bannersSectionRef: bannersSectionRef
                ? { current: bannersSectionRef.current ? 'isSet' : null }
                : undefined,
              bundlesSectionRef: bundlesSectionRef
                ? { current: bundlesSectionRef.current ? 'isSet' : null }
                : undefined,
              productsSectionRef: productsSectionRef
                ? { current: productsSectionRef.current ? 'isSet' : null }
                : undefined,
              membersOnly: {
                accessoriesSectionRef: accessoriesSectionRef
                  ? {
                      current: accessoriesSectionRef.current ? 'isSet' : null
                    }
                  : undefined
              }
            }
          }
        };
      }
    }
  )
);
